import { useMediaQuery } from '@mui/material';
import React, { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';

import CommonLoader from '../../components/common-loader/CommonLoader';
import { Col, Row } from '../../components/layout/layout';
import { SONAR_RANGE_TYPE, SORT_ORDER } from '../../constants';
import { getConsumerSonarData, getConsumerSonarNavig8 } from '../../redux/consumer/sonar/consumerSonarActions';
import { cancelNavig8, resetSonarData, selectSonarTab } from '../../redux/sonar/sonarReducer';
import Brand from '../../sonar/brand/Brand';
import Dashboard from '../../sonar/dashboard/Dashboard';
import DedicatedTableColumn from '../../sonar/dedicated-table/DedicatedTableColumn';
import Engagement from '../../sonar/engagement/Engagement';
import EngagementAll from '../../sonar/engagement/EngagementAll';
import Interest from '../../sonar/interest/Interest';
import InterestAll from '../../sonar/interest/InterestAll';
import Powerrank from '../../sonar/powerrank/Powerrank';
import Presence from '../../sonar/presence/Presence';
import PresenceAll from '../../sonar/presence/PresenceAll';
import Relevance from '../../sonar/relevance/Relevance';
import RelevanceAll from '../../sonar/relevance/RelevanceAll';
import Rivalry from '../../sonar/rivalry/Rivalry';
import RivalryAll from '../../sonar/rivalry/RivalryAll';
import SonarTable from '../../sonar/sonar-table/SonarTable';
import Trust from '../../sonar/trust/Trust';
import TrustAll from '../../sonar/trust/TrustAll';
import { SONAR_DATA_KEYS } from '../../utils/chart';
import timeout from '../../utils/timeout';
import ConsumerLayout from '../consumer-layout/ConsumerLayout';
import Header from './components/header/Header';
import Navig8Consumer from './components/navig8/Navig8Consumer';

import scss from './SonarDashboard.module.scss';

const SonarDashboard = () => {
  const { t } = useTranslation('sonar');
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width: 600px)');

  const {
    [SONAR_RANGE_TYPE.COMPARABLE]: { startDate: comparableStartDate, endDate: comparableEndDate },
    [SONAR_RANGE_TYPE.COMPARE_TO]: { startDate: compareToStartDate, endDate: compareToEndDate },
  } = useSelector((state) => state.sonarDashboard.dateRanges);
  const selectedTab = useSelector((state) => state.sonarDashboard.selectedTab);
  const isLoading = useSelector((state) => state.sonarDashboard.isLoading);
  const isShowDedicated = useSelector((state) => state.sonarDashboard.isShowDedicated);
  const navig8 = useSelector((state) => state.sonarDashboard.navig8);
  const navig8AbortController = useSelector((state) => state.sonarDashboard.navig8AbortController);

  const chartsWidth = isShowDedicated ? 75 : 100;

  const handleChangeTab = (newTab) => {
    dispatch(selectSonarTab(newTab));
  };

  const handleReinitializeNavig8 = async () => {
    dispatch(cancelNavig8());
    await timeout(100);
    dispatch(getConsumerSonarNavig8());
  };

  useEffect(() => {
    dispatch(getConsumerSonarData());
  }, [comparableEndDate, comparableStartDate, compareToEndDate, compareToStartDate, dispatch]);

  useEffect(() => {
    if (navig8.thread || (!navig8.thread && navig8AbortController)) {
      handleReinitializeNavig8();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparableEndDate, comparableStartDate, compareToEndDate, compareToStartDate, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(cancelNavig8());
      dispatch(resetSonarData());
    };
  }, [dispatch]);

  return (
    <ConsumerLayout Header={Header()}>
      <Tabs selectedIndex={selectedTab} onSelect={handleChangeTab} className={scss.tabs}>
        <div className='consumer_tab-list_wrapper '>
          <TabList className='consumer_tab-list'>
            <Tab>{t('dashboard')}</Tab>
            <Tab disabled={isLoading}>NAVIG8</Tab>
            <Tab disabled={isLoading}>{t('powerrank')}</Tab>
            <Tab disabled={isLoading}>{t('data')}</Tab>
            <Tab disabled={isLoading}>{t('interest')}</Tab>
            <Tab disabled={isLoading}>{t('relevance')}</Tab>
            <Tab disabled={isLoading}>{t('trust')}</Tab>
            <Tab disabled={isLoading}>{t('engagement')}</Tab>
            <Tab disabled={isLoading}>{t('rivalry')}</Tab>
            <Tab disabled={isLoading}>{t('presence')}</Tab>
            <Tab disabled={isLoading}>{t('brand')}</Tab>
          </TabList>
        </div>

        <TabPanel>
          <Dashboard />
        </TabPanel>

        <TabPanel>
          <Navig8Consumer />
        </TabPanel>

        <TabPanel>
          <Powerrank />
        </TabPanel>

        <TabPanel>
          <SonarTable classes={{ wrapper: scss.dataTable }} />
        </TabPanel>

        <TabPanel>
          <Row className={scss.row}>
            <Col width={chartsWidth} className={scss.columnContent}>
              <Interest />
              {!isMobile ? <InterestAll /> : null}
            </Col>

            <DedicatedTableColumn
              columnsToShow={['interest']}
              initialSort={{
                key: 'interest',
                order: SORT_ORDER.DESC,
              }}
              rankLabel={`${t('rank')} ${t('interest')}`}
              rankKey={SONAR_DATA_KEYS.INTEREST}
            />
          </Row>
        </TabPanel>

        <TabPanel>
          <Row className={scss.row}>
            <Col width={chartsWidth} className={scss.columnContent}>
              <Relevance />
              {!isMobile ? <RelevanceAll /> : null}
            </Col>

            <DedicatedTableColumn
              columnsToShow={['relevanceStrength', 'relevanceBreadth']}
              initialSort={{
                key: 'relevanceStrength',
                order: SORT_ORDER.DESC,
              }}
              rankLabel={`${t('rank')} ${t('relevanceStrength')}`}
              rankKey={SONAR_DATA_KEYS.RELEVANCE_STRENGTH}
            />
          </Row>
        </TabPanel>

        <TabPanel>
          <Row className={scss.row}>
            <Col width={chartsWidth} className={scss.columnContent}>
              <Trust />
              {!isMobile ? <TrustAll /> : null}
            </Col>

            <DedicatedTableColumn
              columnsToShow={['trust']}
              initialSort={{
                key: 'trust',
                order: SORT_ORDER.DESC,
              }}
              rankLabel={`${t('rank')} ${t('trust')}`}
              rankKey={SONAR_DATA_KEYS.TRUST}
            />
          </Row>
        </TabPanel>

        <TabPanel>
          <Row className={scss.row}>
            <Col width={chartsWidth} className={scss.columnContent}>
              <Engagement />
              {!isMobile ? <EngagementAll /> : null}
            </Col>

            <DedicatedTableColumn
              columnsToShow={['passion']}
              initialSort={{
                key: 'passion',
                order: SORT_ORDER.DESC,
              }}
              rankLabel={`${t('rank')} ${t('engagement')}`}
              rankKey={SONAR_DATA_KEYS.PASSION}
            />
          </Row>
        </TabPanel>

        <TabPanel>
          <Row className={scss.row}>
            <Col width={chartsWidth} className={scss.columnContent}>
              <Rivalry />
              {!isMobile ? <RivalryAll /> : null}
            </Col>

            <DedicatedTableColumn
              columnsToShow={['friction']}
              initialSort={{
                key: 'friction',
                order: SORT_ORDER.DESC,
              }}
              rankLabel={`${t('rank')} ${t('rivalry')}`}
              rankKey={SONAR_DATA_KEYS.FRICTION}
            />
          </Row>
        </TabPanel>

        <TabPanel>
          <Row className={scss.row}>
            <Col width={chartsWidth} className={scss.columnContent}>
              <Presence />
              {!isMobile ? <PresenceAll /> : null}
            </Col>

            <DedicatedTableColumn
              columnsToShow={['presence', 'energy']}
              initialSort={{
                key: 'presence',
                order: SORT_ORDER.DESC,
              }}
              rankLabel={`${t('rank')} ${t('reach')}`}
              rankKey={SONAR_DATA_KEYS.PRESENCE}
            />
          </Row>
        </TabPanel>

        <TabPanel>
          <Row className={scss.row}>
            <Col width={chartsWidth} className={scss.columnContent}>
              <Brand />
            </Col>

            <DedicatedTableColumn columnsToShow={['powerrank']} rankKey={SONAR_DATA_KEYS.POWERRANK} disableGlobalRank />
          </Row>
        </TabPanel>
      </Tabs>

      <CommonLoader isLoading={isLoading} />
    </ConsumerLayout>
  );
};

export default memo(SonarDashboard);
